import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle2, MapPin, ScanSearch, Wind, Thermometer, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import ServiceAreaMap from '../components/ServiceAreaMap';
import {
  COMPANY, IMAGES, SERVICE_AREAS, WHY_US, PROCESS,
  slugify, cityIntro, nearbyCities,
} from '../data/mock';

const CITY_SERVICES = [
  { icon: ScanSearch, title: 'Mold Inspection', desc: 'Certified visual inspection with digital documentation of every area of concern.' },
  { icon: Wind, title: 'Air Quality Testing', desc: 'Indoor air and surface sampling analyzed by accredited third-party labs.' },
  { icon: Thermometer, title: 'Thermal Imaging', desc: 'Infrared cameras reveal hidden moisture behind walls, ceilings and floors.' },
  { icon: FlaskConical, title: 'Lab Analysis', desc: 'Objective, A2LA-accredited results with a clear, plain-language report.' },
];

const CityPage = () => {
  const { city: slug } = useParams();
  const city = SERVICE_AREAS.find((c) => slugify(c) === slug);

  useEffect(() => {
    if (city) {
      document.title = `Mold Inspection & Air Quality Testing in ${city} | ${COMPANY.name}`;
    }
    return () => { document.title = `${COMPANY.name}`; };
  }, [city]);

  if (!city) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <h1 className="text-3xl font-bold text-[#13251b]">Area not found</h1>
        <p className="mt-4 text-[#5b6a60]">We couldn’t find that community — but we likely still serve you.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Button asChild className="bg-brand hover:bg-[#5c9a22] text-white rounded-full px-7 h-12 font-semibold">
            <Link to="/service-areas">View all service areas</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full px-7 h-12 font-semibold border-[#cbdcbb] text-[#13251b]">
            <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
          </Button>
        </div>
      </div>
    );
  }

  const nearby = nearbyCities(city, SERVICE_AREAS, 8);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: `360 Mold Services — ${city}`,
    description: `Mold inspection and indoor air quality testing in ${city}, Ontario.`,
    telephone: '+1-226-972-6440',
    areaServed: { '@type': 'City', name: city },
    url: `https://360moldservices.ca/service-areas/${slugify(city)}`,
  };

  return (
    <div>
      <Seo
        path={`/service-areas/${slugify(city)}`}
        title={`Mold Inspection & Air Quality Testing in ${city}, ON`}
        description={`Certified, independent mold inspection and indoor air quality testing in ${city}, Ontario. Thermal imaging, accredited lab analysis and clear reports. Call (226) 972-6440.`}
        schema={schema}
      />
      {/* HERO */}
      <section className="brand-gradient relative overflow-hidden">
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c7e79b]/40 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="flex items-center gap-2 text-brand font-semibold text-sm">
              <MapPin className="h-4 w-4" /> Serving {city}, Ontario
            </div>
            <h1 className="mt-3 text-4xl lg:text-5xl font-extrabold text-[#13251b] leading-tight">
              Mold Inspection &amp; Air Quality Testing in <span className="text-brand">{city}</span>
            </h1>
            <p className="mt-5 text-lg text-[#44564b] leading-relaxed">{cityIntro(city)}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild className="bg-brand hover:bg-[#5c9a22] text-white rounded-full px-7 h-12 font-semibold">
                <Link to="/contact">Book in {city} <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full px-7 h-12 font-semibold border-[#cbdcbb] text-[#13251b] hover:bg-white">
                <a href={COMPANY.phoneHref}><Phone className="h-4 w-4 mr-1" /> {COMPANY.phone}</a>
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#44564b]">
              {['IAC2-certified inspectors', '3rd-party lab analysis', 'Report within ~1 week'].map((t) => (
                <span key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand" /> {t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <img src={IMAGES.heroHome} alt={`Healthy home interior in ${city}`} className="rounded-[1.75rem] w-full h-[400px] object-cover shadow-xl" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center">
            <span className="text-brand font-semibold text-sm uppercase tracking-wider">In {city}</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#13251b]">What we test for</h2>
            <p className="mt-4 text-[#5b6a60]">Complete, certified environmental assessments for {city} homes and businesses.</p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CITY_SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(i % 4) * 80} className="bg-[#fafcf7] rounded-2xl p-6 border border-[#eef2e8] hover-lift">
                  <div className="h-12 w-12 rounded-xl bg-[#eaf4dd] flex items-center justify-center">
                    <Icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="mt-4 font-semibold text-[#13251b]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#5b6a60] leading-relaxed">{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 bg-[#fafcf7]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-[#13251b]">Why {city} chooses 360 Mold Services</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={i * 80} className="bg-white rounded-2xl p-6 border border-[#eef2e8]">
                  <div className="h-11 w-11 rounded-lg bg-[#eaf4dd] flex items-center justify-center">
                    <Icon className="h-5 w-5 text-brand" />
                  </div>
                  <h4 className="mt-4 font-semibold text-[#13251b]">{w.title}</h4>
                  <p className="mt-2 text-sm text-[#5b6a60] leading-relaxed">{w.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 dark-section text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center mb-12">
            <span className="text-[#8bd44a] font-semibold text-sm uppercase tracking-wider">How it works in {city}</span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold">Simple, transparent, thorough</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 80} className="relative bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="text-5xl font-extrabold text-white/10 font-display absolute top-3 right-4">{i + 1}</div>
                  <div className="h-12 w-12 rounded-xl bg-[#8bd44a]/15 flex items-center justify-center">
                    <Icon className="h-6 w-6 text-[#8bd44a]" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold">{p.title}</h4>
                  <p className="mt-2 text-sm text-[#a9bcae] leading-relaxed">{p.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* NEARBY + MAP */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-12"><ServiceAreaMap /></Reveal>
          <Reveal>
            <h3 className="text-2xl font-bold text-[#13251b] mb-5">We also serve near {city}</h3>
            <div className="flex flex-wrap gap-3">
              {nearby.map((n) => (
                <Link
                  key={n}
                  to={`/service-areas/${slugify(n)}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#eaf4dd] text-[#3a5227] text-sm font-medium hover:bg-brand hover:text-white transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5" /> {n}
                </Link>
              ))}
              <Link to="/service-areas" className="px-4 py-2 rounded-full bg-brand text-white text-sm font-semibold hover:bg-[#5c9a22] transition-colors">
                View all areas
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-20 bg-[#fafcf7]">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <Reveal className="bg-white rounded-[1.5rem] p-7 lg:p-8 shadow-lg border border-[#eef2e8]">
            <h3 className="text-2xl font-bold text-[#13251b]">Book your {city} inspection</h3>
            <p className="mt-1 text-sm text-[#5b6a60] mb-6">Request a free quote and we’ll be in touch within one business day.</p>
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
